// @owma/common-web/components/ReadReceiptDetailPopup — Read Receipt 상세 팝업 본문(presentational).
//
// Figma 79988-2328 구조: 헤더 / status / 탭(All·Read·Unread) / 수신자 목록.
// 다이얼로그 셸과 데이터 공급은 host 소유 — web 은 confirmBase + subscribeReadReceipt 구독,
// desktop 은 OmDialog + TanStack Query. host 의존(i18n·시간 포맷·아바타)은 전부 props 주입.
//
// ⚠ 미러 이식성: 이 폴더는 officemail-platform 저장소 packages/common-web 에 그대로 복사된다.
// app alias(i18n·services·utils·pages·popups·assets)와 @owma/kit import 금지 —
// 허용 import 는 react·classnames·자체 scss·상대 경로 omw 킷(../omw)뿐.
import type {
  ReadReceiptDetailPopupProps,
  ReadReceiptPopupRecipient,
  ReadReceiptPopupStatus,
} from './types'
import { OmwSpinner, OmwTabs } from '../omw'

import styles from './style.module.scss'

type Translate = ReadReceiptDetailPopupProps['t']

// 타입 뱃지·by_type 라벨 키 맵. 미등록 recipient_type 은 대문자 폴백(desktop fieldLabel 동형).
const FIELD_LABEL_KEYS: Partial<Record<string, string>> = {
  to: 'email.field.to',
  cc: 'email.field.cc',
  bcc: 'email.field.bcc',
}

function fieldLabel(t: Translate, type: string): string {
  const key = FIELD_LABEL_KEYS[type]
  // 동적 키(FIELD_LABEL_KEYS 참조) — 추출 대상 키는 web host translation.json 에 이미 존재.
  return key != null ? t(key) /* i18next-extract-disable-line */ : type.toUpperCase()
}

// 수신자 행 읽은 시각(MM-DD HH:mm) — web·desktop 동형이라 컴포넌트 내부 유지.
// web timeFormat 과 동일하게 타임존 표기 없는 서버 시각은 UTC 로 간주해 로컬 시각으로 변환.
function formatShortDateTime(iso: string): string {
  const normalized = iso.includes('Z') || iso.includes('+') ? iso : `${iso}Z`
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return iso
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`
}

function formatByTypeText(t: Translate, status: ReadReceiptPopupStatus): string {
  const parts: string[] = []
  const { to, cc, bcc } = status.by_type
  if (to != null) parts.push(`${fieldLabel(t, 'to')} ${to.read}/${to.total}`)
  if (cc != null) parts.push(`${fieldLabel(t, 'cc')} ${cc.read}/${cc.total}`)
  if (bcc != null) parts.push(`${fieldLabel(t, 'bcc')} ${bcc.read}/${bcc.total}`)
  return parts.join(' | ')
}

interface RecipientListProps {
  recipients: ReadReceiptPopupRecipient[]
  t: Translate
  renderAvatar: ReadReceiptDetailPopupProps['renderAvatar']
}

function RecipientList({ recipients, t, renderAvatar }: RecipientListProps) {
  return (
    <ul className={styles.recipientList}>
      {recipients.map((recipient) => (
        <li key={recipient.receipt_id} className={styles.recipientItem}>
          {renderAvatar(recipient)}
          <div className={styles.recipientInfo}>
            <div className={styles.nameLine}>
              <span className={styles.name}>
                {recipient.recipient_display_name || recipient.recipient_email}
              </span>
              <span className={styles.typeBadge}>
                {fieldLabel(t, recipient.recipient_type).toUpperCase()}
              </span>
            </div>
            <p className={styles.email}>{recipient.recipient_email}</p>
          </div>
          <div className={styles.recipientStatus}>
            {recipient.status === 'read' ? (
              <>
                <span className={styles.statusLabel}>{t('readReceipt.recipient.read')}</span>
                {recipient.first_read_real_at != null && (
                  <span className={styles.statusTime}>
                    {formatShortDateTime(recipient.first_read_real_at)}
                  </span>
                )}
              </>
            ) : (
              <span className={styles.statusLabel}>{t('readReceipt.recipient.unread')}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

export function ReadReceiptDetailPopup({
  status,
  isRefreshing = false,
  t,
  formatDateTime,
  renderAvatar,
}: ReadReceiptDetailPopupProps) {
  const percentageText = `${status.read}/${status.total}`
  const percentLabel = t('readReceipt.popup.readPercent', {
    percent: Math.round(status.read_percentage),
  })
  const progressPercent =
    status.total > 0 ? Math.min(100, Math.max(0, (status.read / status.total) * 100)) : 0
  const byTypeText = formatByTypeText(t, status)

  const readRecipients = status.recipients.filter((r) => r.status === 'read')
  const unreadRecipients = status.recipients.filter((r) => r.status !== 'read')

  const tabItems = [
    {
      key: 'all',
      label: t('readReceipt.tab.all', { count: status.total }),
      children: <RecipientList recipients={status.recipients} t={t} renderAvatar={renderAvatar} />,
    },
    {
      key: 'read',
      label: t('readReceipt.tab.read', { count: status.read }),
      children: <RecipientList recipients={readRecipients} t={t} renderAvatar={renderAvatar} />,
    },
    {
      key: 'unread',
      label: t('readReceipt.tab.unread', { count: status.unread }),
      children: <RecipientList recipients={unreadRecipients} t={t} renderAvatar={renderAvatar} />,
    },
  ]

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleArea}>
          <h2 className={styles.title}>{t('readReceipt.popup.title')}</h2>
          <p className={styles.sentAt}>
            {t('readReceipt.popup.sentAt', { time: formatDateTime(status.sent_at) })}
          </p>
        </div>
      </header>

      <section className={styles.statusSection}>
        <div className={styles.statusLine}>
          <span className={styles.percentage}>{percentageText}</span>
          <span className={styles.percentLabel}>{percentLabel}</span>
          {isRefreshing && (
            <span className={styles.refreshingIndicator}>
              <OmwSpinner size="small" />
            </span>
          )}
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
        </div>
        {byTypeText.length > 0 && <p className={styles.byType}>{byTypeText}</p>}
      </section>

      <OmwTabs defaultActiveKey="all" items={tabItems} className={styles.tabs} destroyOnHidden />
    </div>
  )
}
